<script setup lang="ts">
import {
  AlertTriangle,
  BadgeCheck,
  CreditCard,
  History,
  KeyRound,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  Menu,
  MessageSquare,
  MonitorCog,
  Moon,
  PackageCheck,
  PackageSearch,
  Receipt,
  ShieldCheck,
  Settings,
  Sliders,
  Sun,
  Users,
  X,
} from "@lucide/vue";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { RouterLink, RouterView, useRouter } from "vue-router";
import type { Role } from "../lib/types";
import { api } from "../lib/api";
import { useAuthStore } from "../stores/auth";
import { useThemeStore } from "../stores/theme";

const auth = useAuthStore();
const theme = useThemeStore();
const router = useRouter();
const open = ref(false);
const menuButton = ref<HTMLButtonElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);

// Indicador de conexión real: ping periódico a /health.
const connected = ref(true);
let healthTimer: number | undefined;
async function checkHealth() {
  try {
    await api.get("/health", { timeout: 6000 });
    connected.value = true;
  } catch {
    connected.value = false;
  }
}
onMounted(() => {
  checkHealth();
  healthTimer = window.setInterval(checkHealth, 20000);
  window.addEventListener("keydown", onWindowKeydown);
});
onUnmounted(() => {
  if (healthTimer) clearInterval(healthTimer);
  window.removeEventListener("keydown", onWindowKeydown);
  document.body.classList.remove("menu-open");
});

function closeMenu() {
  open.value = false;
}

function onWindowKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && open.value) closeMenu();
}

watch(open, async (isOpen) => {
  document.body.classList.toggle("menu-open", isOpen);
  if (isOpen) {
    await nextTick();
    closeButton.value?.focus();
  } else {
    await nextTick();
    menuButton.value?.focus();
  }
});

const items: {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles: Role[];
}[] = [
  { to: "/", label: "Resumen", icon: LayoutDashboard, roles: ["admin"] },
  { to: "/usuarios", label: "Usuarios", icon: Users, roles: ["admin"] },
  { to: "/licencias", label: "Licencias", icon: BadgeCheck, roles: ["admin", "operator", "viewer"] },
  { to: "/equipos", label: "Equipos", icon: MonitorCog, roles: ["admin", "operator", "viewer"] },
  { to: "/historial", label: "Historial", icon: History, roles: ["admin"] },
  { to: "/otp", label: "Diagnóstico OTP", icon: ShieldCheck, roles: ["admin"] },
  { to: "/planes", label: "Razones y planes", icon: PackageCheck, roles: ["admin", "operator"] },
  { to: "/mercadopago", label: "Mercado Pago", icon: CreditCard, roles: ["admin", "operator"] },
  { to: "/variables", label: "Variables", icon: Sliders, roles: ["admin", "operator"] },
  { to: "/tokens", label: "Tokens", icon: KeyRound, roles: ["admin", "operator"] },
  { to: "/errores", label: "Errores", icon: AlertTriangle, roles: ["admin", "operator"] },
  { to: "/sugerencias", label: "Sugerencias", icon: Lightbulb, roles: ["admin", "operator"] },
  { to: "/tickets", label: "Tickets OCR", icon: Receipt, roles: ["admin", "operator"] },
  { to: "/productos", label: "Productos", icon: PackageSearch, roles: ["admin", "operator"] },
  { to: "/chat", label: "Chat en Vivo", icon: MessageSquare, roles: ["admin", "operator"] },
  { to: "/configuracion", label: "Configuración", icon: Settings, roles: ["admin", "operator", "viewer"] },
];

const navigation = computed(() =>
  items.filter((item) => Boolean(auth.user && item.roles.includes(auth.user.role))),
);

async function logout() {
  await auth.logout();
  await router.replace("/login");
}
</script>

<template>
  <div class="app-shell">
    <a class="skip-link" href="#main-content">Saltar al contenido</a>
    <button v-if="open" class="mobile-overlay" aria-label="Cerrar menú" @click="closeMenu" />
    <aside :class="['sidebar', { open }]" aria-label="Navegación principal">
      <div class="brand">
        <span>iS</span>
        <div>
          <strong>iSyLicencias</strong>
          <small>Control operativo</small>
        </div>
        <button ref="closeButton" class="mobile-close" aria-label="Cerrar navegación" @click="closeMenu">
          <X :size="20" />
        </button>
      </div>
      <nav>
        <RouterLink
          v-for="item in navigation"
          :key="item.to"
          :to="item.to"
          :aria-current="$route.path === item.to || ($route.path.startsWith(`${item.to}/`) && item.to !== '/') ? 'page' : undefined"
          @click="closeMenu"
        >
          <component :is="item.icon" :size="19" />
          {{ item.label }}
        </RouterLink>
      </nav>
      <div class="account">
        <span>{{ auth.user?.email }}</span>
        <small>{{ auth.user?.role }}</small>
        <button @click="logout"><LogOut :size="17" /> Cerrar sesión</button>
      </div>
    </aside>
    <div class="content-column">
      <header class="topbar">
        <button ref="menuButton" class="menu-button" aria-label="Abrir navegación" :aria-expanded="open" @click="open = true">
          <Menu :size="20" />
        </button>
        <span
          :class="['connection-dot', { 'is-offline': !connected }]"
          role="status"
          aria-live="polite"
        >{{ connected ? "Operación conectada" : "Sin conexión con el servidor" }}</span>
        <button class="icon-button" aria-label="Cambiar tema" @click="theme.toggle()">
          <Sun v-if="theme.theme === 'dark'" :size="18" />
          <Moon v-else :size="18" />
        </button>
      </header>
      <main id="main-content" class="main-content" tabindex="-1"><RouterView /></main>
    </div>
  </div>
</template>
